import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import styles from './index.module.scss';

const DraggableElement = ({ element, index, isSelected, onSelect, onChange, onPosterChange, onDragStart, onDragEnd, aspectRatioLocked, originalAspectRatio }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0, offsetX: 0, offsetY: 0 });
    const [resizeCorner, setResizeCorner] = useState(null);
    const elementRef = useRef(null);
    const animationFrameRef = useRef(null);
    const lastUpdateRef = useRef({ x: element.x, y: element.y, w: element.w, h: element.h });

    // 计算宽高比
    const calculateAspectRatio = (width, height) => {
        return width / height;
    };

    // 根据宽高比计算新的尺寸
    const calculateSizeByAspectRatio = (width, height, aspectRatio, isWidthChange) => {
        if (isWidthChange) {
            return { width, height: width / aspectRatio };
        } else {
            return { width: height * aspectRatio, height };
        }
    };

    const [{ isDragging: dragIsDragging }, drag] = useDrag({
        type: 'POSTER_ELEMENT',
        item: { index, type: element.i !== undefined ? 'image' : 'text' },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    // 防抖更新函数
    const debouncedUpdate = useCallback((updates) => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        animationFrameRef.current = requestAnimationFrame(() => {
            const hasChanges = Object.keys(updates).some(key =>
                lastUpdateRef.current[key] !== updates[key]
            );

            if (hasChanges) {
                onChange(index, updates);
                lastUpdateRef.current = { ...lastUpdateRef.current, ...updates };
            }
        });
    }, [index, onChange]);

    const handleMouseDown = useCallback((e) => {
        // 检查是否点击了调整大小的手柄
        if (e.target.classList.contains(styles.resizeHandle)) {
            return; // 让resize handle处理
        }

        // 检查是否点击了元素标签
        if (e.target.classList.contains(styles.elementLabel)) {
            return; // 让元素标签处理
        }

        e.preventDefault();
        e.stopPropagation();
        onSelect(index);

        const rect = elementRef.current.getBoundingClientRect();
        const canvas = elementRef.current.parentElement;
        const canvasRect = canvas.getBoundingClientRect();

        // 计算鼠标在元素内的偏移
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;

        setDragOffset({ x: offsetX, y: offsetY });
        setIsDragging(true);

        // 触发拖动开始回调
        if (onDragStart) {
            onDragStart(element);
        }
    }, [element, index, onSelect, onDragStart]);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging || !elementRef.current) return;

        const canvas = elementRef.current.parentElement;
        const canvasRect = canvas.getBoundingClientRect();

        // 使用偏移量计算新位置
        const newX = e.clientX - canvasRect.left - dragOffset.x;
        const newY = e.clientY - canvasRect.top - dragOffset.y;

        // 确保元素不会超出画布边界
        const clampedX = Math.max(0, Math.min(newX, canvasRect.width - element.w));
        const clampedY = Math.max(0, Math.min(newY, canvasRect.height - element.h));

        debouncedUpdate({ x: clampedX, y: clampedY });
    }, [isDragging, dragOffset, element.w, element.h, debouncedUpdate]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        // 触发拖动结束回调
        if (onDragEnd) {
            onDragEnd();
        }
    }, [onDragEnd]);

    const handleResizeStart = useCallback((e, corner) => {
        e.stopPropagation();
        e.preventDefault();

        const rect = elementRef.current.getBoundingClientRect();
        const canvas = elementRef.current.parentElement;
        const canvasRect = canvas.getBoundingClientRect();

        // 计算鼠标在元素内的偏移
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;

        setResizeStart({
            x: e.clientX,
            y: e.clientY,
            width: element.w,
            height: element.h,
            offsetX,
            offsetY
        });
        setResizeCorner(corner);
        setIsResizing(true);
    }, [element.w, element.h]);

    const handleResizeMove = useCallback((e) => {
        if (!isResizing || !resizeCorner) return;

        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;

        let newWidth = resizeStart.width;
        let newHeight = resizeStart.height;
        let newX = element.x;
        let newY = element.y;

        // 根据拖拽的角落调整大小，保持元素中心不变
        switch (resizeCorner) {
            case 'topLeft':
                newWidth = Math.max(20, resizeStart.width - deltaX);
                newHeight = Math.max(20, resizeStart.height - deltaY);
                // 保持右下角位置不变
                newX = element.x + element.w - newWidth;
                newY = element.y + element.h - newHeight;
                break;
            case 'topRight':
                newWidth = Math.max(20, resizeStart.width + deltaX);
                newHeight = Math.max(20, resizeStart.height - deltaY);
                // 保持左下角位置不变
                newY = element.y + element.h - newHeight;
                break;
            case 'bottomLeft':
                newWidth = Math.max(20, resizeStart.width - deltaX);
                newHeight = Math.max(20, resizeStart.height + deltaY);
                // 保持右上角位置不变
                newX = element.x + element.w - newWidth;
                break;
            case 'bottomRight':
                newWidth = Math.max(20, resizeStart.width + deltaX);
                newHeight = Math.max(20, resizeStart.height + deltaY);
                // 保持左上角位置不变
                break;
        }

        // 如果启用了比例锁定，根据宽高比调整尺寸
        if (aspectRatioLocked && originalAspectRatio && element.i !== undefined) {
            const aspectRatio = originalAspectRatio;

            // 根据拖拽的角落决定以哪个维度为主
            // 使用累计变化量来避免跳变，增加阈值来减少频繁切换
            const totalDeltaX = Math.abs(e.clientX - resizeStart.x);
            const totalDeltaY = Math.abs(e.clientY - resizeStart.y);
            const changeThreshold = 5; // 5像素的阈值

            let isWidthChange = true;
            switch (resizeCorner) {
                case 'topLeft':
                case 'topRight':
                case 'bottomLeft':
                case 'bottomRight':
                    // 使用累计变化量和阈值来决定主导方向，避免频繁切换
                    if (Math.abs(totalDeltaX - totalDeltaY) > changeThreshold) {
                        isWidthChange = totalDeltaX > totalDeltaY;
                    } else {
                        // 变化量相近时，默认以宽度为主
                        isWidthChange = true;
                    }
                    break;
            }

            if (isWidthChange) {
                // 以宽度为主，计算高度
                newHeight = newWidth / aspectRatio;
                // 重新计算Y坐标以保持正确的角落位置
                switch (resizeCorner) {
                    case 'topLeft':
                    case 'topRight':
                        newY = element.y + element.h - newHeight;
                        break;
                }
            } else {
                // 以高度为主，计算宽度
                newWidth = newHeight * aspectRatio;
                // 重新计算X坐标以保持正确的角落位置
                switch (resizeCorner) {
                    case 'topLeft':
                    case 'bottomLeft':
                        newX = element.x + element.w - newWidth;
                        break;
                }
            }
        }

        // 确保元素不会超出画布边界
        const canvas = elementRef.current.parentElement;
        const canvasRect = canvas.getBoundingClientRect();

        if (newX < 0) {
            newWidth += newX;
            newX = 0;
        }
        if (newY < 0) {
            newHeight += newY;
            newY = 0;
        }
        if (newX + newWidth > canvasRect.width) {
            newWidth = canvasRect.width - newX;
        }
        if (newY + newHeight > canvasRect.height) {
            newHeight = canvasRect.height - newY;
        }

        // 确保最小尺寸并四舍五入到整数
        newWidth = Math.max(20, Math.round(newWidth));
        newHeight = Math.max(20, Math.round(newHeight));
        newX = Math.round(newX);
        newY = Math.round(newY);

        debouncedUpdate({ x: newX, y: newY, w: newWidth, h: newHeight });
    }, [isResizing, resizeCorner, resizeStart, element.x, element.y, element.w, element.h, debouncedUpdate, aspectRatioLocked, originalAspectRatio]);

    const handleResizeEnd = useCallback(() => {
        setIsResizing(false);
        setResizeCorner(null);
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
    }, []);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'grabbing';
            document.body.style.userSelect = 'none';

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleResizeMove);
            document.addEventListener('mouseup', handleResizeEnd);
            document.body.style.userSelect = 'none';

            return () => {
                document.removeEventListener('mousemove', handleResizeMove);
                document.removeEventListener('mouseup', handleResizeEnd);
                document.body.style.userSelect = '';
            };
        }
    }, [isResizing, handleResizeMove, handleResizeEnd]);

    // 清理动画帧
    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    const elementStyle = {
        left: element.x,
        top: element.y,
        width: element.w,
        height: element.h,
        borderRadius: element.r || 0,
        transform: isDragging ? 'scale(1.02)' : 'scale(1)',
        transition: isDragging || isResizing ? 'none' : 'transform 0.1s ease, border-color 0.2s ease',
        cursor: isDragging ? 'grabbing' : 'grab',
    };

    if (element.i !== undefined) {
        // 图片元素
        elementStyle.backgroundImage = element.i ? `url(${element.i})` : 'none';
        elementStyle.backgroundColor = element.i ? 'transparent' : '#f0f0f0';
    } else {
        // 文字元素
        elementStyle.color = element.c || '#000000';
        elementStyle.fontSize = `${element.s || 16}px`;
        elementStyle.fontWeight = element.f || 'normal';
        elementStyle.textAlign = element.a || 'left';
        elementStyle.lineHeight = '1.2';
        elementStyle.cursor = isDragging ? 'grabbing' : 'grab';
    }

    return (
        <div
            ref={(node) => {
                drag(node);
                elementRef.current = node;
            }}
            className={`${styles.posterElement} ${element.i !== undefined ? styles.imageElement : styles.textElement} ${isSelected ? styles.selected : ''} ${isDragging ? styles.dragging : ''} ${isResizing ? styles.resizing : ''}`}
            style={elementStyle}
            onMouseDown={handleMouseDown}
        >
            {element.i !== undefined ? (
                // 图片元素
                <>
                    {!element.i && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: '#999',
                            fontSize: '14px'
                        }}>
                            请选择图片
                        </div>
                    )}
                    {isSelected && (
                        <>
                            <div
                                className={`${styles.resizeHandle} ${styles.topLeft} ${aspectRatioLocked ? styles.aspectRatioLocked : ''}`}
                                onMouseDown={(e) => handleResizeStart(e, 'topLeft')}
                            />
                            <div
                                className={`${styles.resizeHandle} ${styles.topRight} ${aspectRatioLocked ? styles.aspectRatioLocked : ''}`}
                                onMouseDown={(e) => handleResizeStart(e, 'topRight')}
                            />
                            <div
                                className={`${styles.resizeHandle} ${styles.bottomLeft} ${aspectRatioLocked ? styles.aspectRatioLocked : ''}`}
                                onMouseDown={(e) => handleResizeStart(e, 'bottomLeft')}
                            />
                            <div
                                className={`${styles.resizeHandle} ${styles.bottomRight} ${aspectRatioLocked ? styles.aspectRatioLocked : ''}`}
                                onMouseDown={(e) => handleResizeStart(e, 'bottomRight')}
                            />
                            <div className={styles.elementLabel}>图片</div>
                        </>
                    )}
                </>
            ) : (
                // 文字元素
                <>
                    <div
                        style={{
                            padding: '8px',
                            wordBreak: 'break-word',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: element.a === 'center' ? 'center' :
                                element.a === 'right' ? 'flex-end' : 'flex-start',
                            pointerEvents: 'none', // 防止文字内容阻止拖拽
                            userSelect: 'none' // 防止文字被选中
                        }}
                    >
                        <div style={{
                            textAlign: element.a || 'left',
                            width: '100%'
                        }}>
                            {element.t || '请输入文字'}
                        </div>
                    </div>
                    {isSelected && (
                        <>
                            <div className={`${styles.resizeHandle} ${styles.topLeft}`} onMouseDown={(e) => handleResizeStart(e, 'topLeft')} />
                            <div className={`${styles.resizeHandle} ${styles.topRight}`} onMouseDown={(e) => handleResizeStart(e, 'topRight')} />
                            <div className={`${styles.resizeHandle} ${styles.bottomLeft}`} onMouseDown={(e) => handleResizeStart(e, 'bottomLeft')} />
                            <div className={`${styles.resizeHandle} ${styles.bottomRight}`} onMouseDown={(e) => handleResizeStart(e, 'bottomRight')} />
                            <div className={styles.elementLabel}>文字</div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

const DroppableCanvas = ({ config, selectedElementIndex, onElementSelect, onElementChange, onPosterChange, aspectRatioLocked, originalAspectRatios }) => {
    const canvasRef = useRef(null);
    const [alignmentLines, setAlignmentLines] = useState({ vertical: null, horizontal: null });
    const [isResizingCanvas, setIsResizingCanvas] = useState(false);
    const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

    const [{ isOver }, drop] = useDrop({
        accept: 'POSTER_ELEMENT',
        drop: (item, monitor) => {
            // 处理拖拽放置逻辑
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    });

    const handleCanvasClick = (e) => {
        if (e.target === canvasRef.current) {
            onElementSelect(null);
        }
    };

    // 画布大小调整相关函数
    const handleCanvasResizeStart = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();

        setResizeStart({
            x: e.clientX,
            y: e.clientY,
            width: config.w,
            height: config.h
        });
        setIsResizingCanvas(true);
    }, [config.w, config.h]);

    const handleCanvasResizeMove = useCallback((e) => {
        if (!isResizingCanvas) return;

        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;

        // 计算新的画布尺寸，保持宽高比（可选）
        const newWidth = Math.max(200, resizeStart.width + deltaX);
        const newHeight = Math.max(200, resizeStart.height + deltaY);

        // 更新画布尺寸
        onPosterChange({ w: newWidth, h: newHeight });
    }, [isResizingCanvas, resizeStart, onPosterChange]);

    const handleCanvasResizeEnd = useCallback(() => {
        setIsResizingCanvas(false);
    }, []);

    // 监听画布大小调整的鼠标事件
    useEffect(() => {
        if (isResizingCanvas) {
            document.addEventListener('mousemove', handleCanvasResizeMove);
            document.addEventListener('mouseup', handleCanvasResizeEnd);
            document.body.style.cursor = 'se-resize';
            document.body.style.userSelect = 'none';

            return () => {
                document.removeEventListener('mousemove', handleCanvasResizeMove);
                document.removeEventListener('mouseup', handleCanvasResizeEnd);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            };
        }
    }, [isResizingCanvas, handleCanvasResizeMove, handleCanvasResizeEnd]);

    // 计算对齐线
    const calculateAlignmentLines = useCallback((draggedElement, allElements) => {
        if (!draggedElement) return { vertical: null, horizontal: null };

        const tolerance = 5; // 对齐容差
        const draggedRect = {
            left: draggedElement.x,
            right: draggedElement.x + draggedElement.w,
            top: draggedElement.y,
            bottom: draggedElement.y + draggedElement.h,
            centerX: draggedElement.x + draggedElement.w / 2,
            centerY: draggedElement.y + draggedElement.h / 2
        };

        let verticalLine = null;
        let horizontalLine = null;

        allElements.forEach((element, index) => {
            if (index === selectedElementIndex) return;

            const elementRect = {
                left: element.x,
                right: element.x + element.w,
                top: element.y,
                bottom: element.y + element.h,
                centerX: element.x + element.w / 2,
                centerY: element.y + element.h / 2
            };

            // 检查垂直对齐
            if (Math.abs(draggedRect.left - elementRect.left) < tolerance) {
                verticalLine = { x: elementRect.left, type: 'left' };
            } else if (Math.abs(draggedRect.right - elementRect.right) < tolerance) {
                verticalLine = { x: elementRect.right, type: 'right' };
            } else if (Math.abs(draggedRect.centerX - elementRect.centerX) < tolerance) {
                verticalLine = { x: elementRect.centerX, type: 'center' };
            }

            // 检查水平对齐
            if (Math.abs(draggedRect.top - elementRect.top) < tolerance) {
                horizontalLine = { y: elementRect.top, type: 'top' };
            } else if (Math.abs(draggedRect.bottom - elementRect.bottom) < tolerance) {
                horizontalLine = { y: elementRect.bottom, type: 'bottom' };
            } else if (Math.abs(draggedRect.centerY - elementRect.centerY) < tolerance) {
                horizontalLine = { y: elementRect.centerY, type: 'center' };
            }
        });

        return { vertical: verticalLine, horizontal: horizontalLine };
    }, [selectedElementIndex]);

    const canvasStyle = {
        width: config.w,
        height: config.h,
        backgroundImage: config.image ? `url(${config.image})` : 'none',
        backgroundColor: config.bg || '#ffffff',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
    };

    return (
        <div
            ref={(node) => {
                drop(node);
                canvasRef.current = node;
            }}
            className={styles.posterCanvas}
            style={canvasStyle}
            onClick={handleCanvasClick}
        >
            {config.content.map((element, index) => (
                <DraggableElement
                    key={index}
                    element={element}
                    index={index}
                    isSelected={selectedElementIndex === index}
                    onSelect={onElementSelect}
                    onChange={onElementChange}
                    onPosterChange={onPosterChange}
                    aspectRatioLocked={aspectRatioLocked[index]}
                    originalAspectRatio={originalAspectRatios[index]}
                    onDragStart={(draggedElement) => {
                        const lines = calculateAlignmentLines(draggedElement, config.content);
                        setAlignmentLines(lines);
                    }}
                    onDragEnd={() => {
                        setAlignmentLines({ vertical: null, horizontal: null });
                    }}
                />
            ))}

            {/* 对齐线 */}
            {alignmentLines.vertical && (
                <div
                    className={styles.alignmentLine}
                    style={{
                        left: alignmentLines.vertical.x,
                        top: 0,
                        height: '100%',
                        width: 1,
                        background: '#1890ff',
                        position: 'absolute',
                        zIndex: 999,
                        pointerEvents: 'none'
                    }}
                />
            )}
            {alignmentLines.horizontal && (
                <div
                    className={styles.alignmentLine}
                    style={{
                        top: alignmentLines.horizontal.y,
                        left: 0,
                        width: '100%',
                        height: 1,
                        background: '#1890ff',
                        position: 'absolute',
                        zIndex: 999,
                        pointerEvents: 'none'
                    }}
                />
            )}

            {/* 画布大小调整手柄 */}
            <div
                className={styles.canvasResizeHandle}
                onMouseDown={handleCanvasResizeStart}
                title="拖拽调整画布大小"
            />

            {config.content.length === 0 && (
                <div className={styles.placeholder}>
                    <div className={styles.icon}>📄</div>
                    <div>点击上方按钮添加图片或文字元素</div>
                </div>
            )}
        </div>
    );
};

export default DroppableCanvas; 